import React from 'react';
import _ from 'lodash';

import {default as Modal, withModal} from 'components/Modal';

import Avatar from 'components/Avatar';
import Button from 'components/Button';

import {
  fetchBookingNotes,
  selectIsAddBookingNoteFormOpened,
  selectOtherBookingNotes,
  toggleAddBookingNoteForm,
  addBookingNote,
  restoreBookingNote,
  archiveBookingNote,
  editBookingNote,
  selectBookingNoteForEdit,
  setBookingNoteForEdit,
  fetchOtherBookingNotes,
  resolveBookingNote
} from 'ducks/notes';

import {
  ResolvableNoteBlock,
  AddNoteButton,
  RequiresActionNoteForm,
  NotesFilters,
  BookingPeriod
} from 'components/Notes';

import {requiresPermission, PERMISSIONS} from 'ducks/auth';

import {MODALS as BASE_MODALS} from 'ducks/modals';

import {reverse} from 'utils/urls';
import {BOOKING_DETAIL_URL} from 'config/urls';

import sortDownButton from 'assets/icons/sort-down-button.svg';
import sortUpButton from 'assets/icons/sort-up-button.svg';

import './styles.css';

const Tenant = ({avatarCropped, internalAvatarCropped, initials, fullName}) => (
  <div className="Tenant">
    <Avatar
      url={internalAvatarCropped || avatarCropped}
      initials={initials}
      width={64}
    />
    <div className="fullName">{fullName}</div>
  </div>
);

const TenantsBar = ({primaryTenant, secondaryTenant}) => (
  <div className="TenantsBar">
    {primaryTenant && (
      <Tenant
        avatarCropped={primaryTenant.avatarCropped}
        internalAvatarCropped={primaryTenant.internalAvatarCropped}
        initials={primaryTenant.initials}
        fullName={primaryTenant.fullName}
      />
    )}
    {secondaryTenant && (
      <Tenant
        avatarCropped={secondaryTenant.avatarCropped}
        internalAvatarCropped={primaryTenant.internalAvatarCropped}
        initials={secondaryTenant.initials}
        fullName={secondaryTenant.fullName}
      />
    )}
  </div>
);

class BookingNotesModal extends React.Component<any> {
  state = {
    showOtherBookings: false
  };

  componentDidMount() {
    this.fetchBookingNotes();
    this.fetchOtherBookingNotes();
  }

  fetchOtherBookingNotesExtraActions = this.props.storeDataExtraActions(
    data => ({
      otherBookingNotes: data
    })
  );

  fetchOtherBookingNotes = () => {
    const {data: {bookingId}} = this.props;

    this.props.fetchOtherBookingNotes(
      {bookingId},
      this.fetchOtherBookingNotesExtraActions
    );
  };

  toggleOtherBookingNotes = () =>
    this.setState({showOtherBookings: !this.state.showOtherBookings});

  fetchBookingNotesExtraActions = this.props.storeDataExtraActions(data => ({
    bookingNotes: data
  }));

  fetchBookingNotes = () => {
    const {data: {bookingId}} = this.props;

    this.props.fetchBookingNotes(
      {bookingId, archived: false},
      this.fetchBookingNotesExtraActions
    );
  };

  toggleAddBookingNoteForm = ({isOpened}) => {
    const {data: {locationRef}} = this.props;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.CREATE_BOOKING_NOTE,
        locationRef
      },
      () => {
        return this.props.toggleAddBookingNoteForm({isOpened: isOpened});
      }
    );
  };

  filterActiveNotes = () => {
    const {data: {bookingId}} = this.props;
    this.props.fetchBookingNotes(
      {bookingId, archived: false},
      this.fetchBookingNotesExtraActions
    );

    this.props.fetchOtherBookingNotes(
      {bookingId, archived: false},
      this.fetchOtherBookingNotesExtraActions
    );
  };

  filterArchivedNotes = () => {
    const {data: {bookingId}} = this.props;
    this.props.fetchBookingNotes(
      {bookingId, archived: true},
      this.fetchBookingNotesExtraActions
    );

    this.props.fetchOtherBookingNotes(
      {bookingId, archived: true},
      this.fetchOtherBookingNotesExtraActions
    );
  };

  submitAddNoteForm = ({text, requiresAction}) => {
    const {data: {bookingId, refetchExtraAction}} = this.props;

    const extraActions = [
      () => toggleAddBookingNoteForm({isOpened: false}),
      () =>
        fetchBookingNotes(
          {bookingId, archived: false},
          this.fetchBookingNotesExtraActions
        ),
      ...refetchExtraAction()
    ];

    this.props.addBookingNote({bookingId, text, requiresAction}, extraActions);
  };

  restoreNote = ({noteId}) => {
    const {data: {bookingId, locationRef, refetchExtraAction}} = this.props;

    const extraActions = [
      () =>
        fetchBookingNotes(
          {bookingId, archived: true},
          this.fetchBookingNotesExtraActions
        ),
      ...refetchExtraAction()
    ];

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.EDIT_BOOKING_NOTE,
        locationRef
      },
      () => {
        this.props.restoreBookingNote({note: noteId}, extraActions);
      }
    );
  };

  archiveNote = ({noteId}) => {
    const {data: {bookingId, locationRef, refetchExtraAction}} = this.props;

    const extraActions = [
      () =>
        fetchBookingNotes(
          {bookingId, archived: false},
          this.fetchBookingNotesExtraActions
        ),
      ...refetchExtraAction()
    ];

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.EDIT_BOOKING_NOTE,
        locationRef
      },
      () => {
        this.props.archiveBookingNote({note: noteId}, extraActions);
      }
    );
  };

  submitEditNoteForm = ({text, noteId, requiresAction}) => {
    const {data: {bookingId, refetchExtraAction}} = this.props;

    this.props.editBookingNote({text, note: noteId, requiresAction}, [
      () => setBookingNoteForEdit({noteId: null}),
      () =>
        fetchBookingNotes(
          {bookingId, archived: false},
          this.fetchBookingNotesExtraActions
        ),
      ...refetchExtraAction()
    ]);
  };

  setNoteForEdit = ({noteId}) => {
    const {data: {locationRef}} = this.props;
    this.props.requiresPermission(
      {
        permission: PERMISSIONS.EDIT_BOOKING_NOTE,
        locationRef: locationRef
      },
      () => {
        this.props.setBookingNoteForEdit({noteId});
      }
    );
  };

  resolveNote = ({noteId}) => {
    const {data: {bookingId, locationRef, refetchExtraAction}} = this.props;

    const extraActions = [
      () =>
        fetchOtherBookingNotes(
          {bookingId},
          this.fetchOtherBookingNotesExtraActions
        ),
      () =>
        fetchBookingNotes(
          {bookingId, archived: false},
          this.fetchBookingNotesExtraActions
        ),
      ...refetchExtraAction()
    ];

    this.props.requiresPermission(
      {permission: PERMISSIONS.RESOLVE_BOOKING_NOTE, locationRef},
      () => {
        this.props.resolveBookingNote({noteId}, extraActions);
      }
    );
  };

  redirectToBookingDetail = ({bookingId}) => {
    const {data: {locationRef}} = this.props;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_BOOKING, locationRef},
      () => {
        this.props.push(
          reverse(BOOKING_DETAIL_URL, {
            locationRef,
            bookingId
          })
        );
      }
    );
  };

  render() {
    const {
      data: {
        primaryTenant,
        secondaryTenant,
        notes,
        isAddNoteFormOpened,
        otherBookingNotes
      }
    } = this.props;
    const {showOtherBookings} = this.state;

    return (
      <Modal
        show={true}
        onClose={this.props.closeModal}
        type={Modal.EXTRA_SMALL}
        className="BookingNotesModal">
        <Modal.Header heading="Booking Notes" />
        <Modal.Body>
          <TenantsBar
            primaryTenant={primaryTenant}
            secondaryTenant={secondaryTenant}
          />
          {!isAddNoteFormOpened && (
            <div className="actions">
              <AddNoteButton
                openAddNoteForm={() =>
                  this.toggleAddBookingNoteForm({isOpened: true})
                }
              />
              <NotesFilters
                filterActiveNotes={this.filterActiveNotes}
                filterArchivedNotes={this.filterArchivedNotes}
              />
            </div>
          )}
          {isAddNoteFormOpened && (
            <RequiresActionNoteForm
              closeForm={() => this.toggleAddBookingNoteForm({isOpened: false})}
              submitAction={this.submitAddNoteForm}
              formName="addBookingNote"
            />
          )}
          {!_.isEmpty(notes) && <div className="time">Now</div>}
          <div className="notes">
            {notes.map(note => (
              <ResolvableNoteBlock
                key={note.id}
                note={note}
                archiveNote={this.archiveNote}
                restoreNote={this.restoreNote}
                submitEditNoteForm={this.submitEditNoteForm}
                setNoteForEdit={this.setNoteForEdit}
                addOptions={true}
                editNote={true}
                editNoteSelector={selectBookingNoteForEdit}
                resolveNote={this.resolveNote}
                editFormName="editBookingNote"
              />
            ))}
          </div>
          {!_.isEmpty(notes) && (
            <div className="time">The beginning of time</div>
          )}

          <div className="showOtherBookingsButtonContainer">
            <Button
              white
              className="showOtherBookingsButton"
              onClick={this.toggleOtherBookingNotes}>
              <div>Other booking notes</div>
              <img
                src={showOtherBookings ? sortUpButton : sortDownButton}
                alt="other bookings"
              />
            </Button>
          </div>
          <div className="otherBookingNotes">
            {showOtherBookings &&
              otherBookingNotes.map(booking => (
                <div key={booking.id} className="notesPerBooking">
                  <BookingPeriod
                    start={booking.periodStart}
                    end={booking.periodEnd}
                    bookingId={booking.id}
                    redirectToBookingDetail={this.redirectToBookingDetail}
                  />

                  {booking.notes.map(note => (
                    <ResolvableNoteBlock
                      key={note.id}
                      note={note}
                      addOptions={false}
                      resolveNote={this.resolveNote}
                    />
                  ))}
                </div>
              ))}
          </div>
        </Modal.Body>
      </Modal>
    );
  }
}

const mapStateToModalData = (state, data) => {
  const primaryTenant = _.get(data, 'bookingNotes.primaryTenant');
  const secondaryTenant = _.get(data, 'bookingNotes.secondaryTenant');
  const notes = _.get(data, 'bookingNotes.notes', []);

  const isAddNoteFormOpened = selectIsAddBookingNoteFormOpened(state);
  const otherBookingNotes = selectOtherBookingNotes(state);

  return {
    primaryTenant,
    secondaryTenant,
    notes,
    isAddNoteFormOpened,
    otherBookingNotes
  };
};

const mapDispatchToProps = {
  fetchBookingNotes,
  requiresPermission,
  toggleAddBookingNoteForm,
  addBookingNote,
  restoreBookingNote,
  archiveBookingNote,
  editBookingNote,
  setBookingNoteForEdit,
  fetchOtherBookingNotes,
  resolveBookingNote
};

export default withModal(
  BASE_MODALS.BOOKING_NOTES,
  mapStateToModalData,
  mapDispatchToProps
)(BookingNotesModal);
